/**
 * PricingTab — section "Tarification" de la page Réglages.
 *
 * Refonte 2026-05-16 (plan iridescent-sprouting-graham) — modèle
 * tier-profit-absolu unique. L'utilisateur entre directement le
 * **bénéfice atelier net minimum** qu'il veut toucher par tranche de
 * coût Gelato. Le système calcule le retail nécessaire pour garantir
 * ce net après absorption shipping zone gratuite + fees Etsy.
 *
 * Le sélecteur 3-modes (percentage / fixed / tiered) a été supprimé —
 * 1 seul shape de formulaire.
 *
 * Au save, le BE déclenche un refresh massif des listings Etsy actifs
 * (recalcul + PATCH price) et clear le flag `migration_review_pending`
 * (la bannière jaune disparaît).
 *
 * Pour le défaut digital : changer la valeur applique une homothétie
 * proportionnelle sur TOUTES les variantes digitales existantes (rescale).
 *
 * Tab move 2026-05-16 : les 5 blocs Etsy (shipping profile, readiness,
 * return policy, frame colors priority, free shipping zones) ont migré
 * vers `<PrintableSpecsTab>` (onglet "Catalogue POD"), qui est leur
 * vrai domaine fonctionnel. PricingTab garde uniquement ce qui fait
 * calcul de prix : profit_tiers + fees + digital_default.
 */

import { FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { z } from 'zod'

import Button from '@/components/ui/Button'
import Field from '@/components/ui/Field'
import Input from '@/components/ui/Input'
import { usePodPricingConfig, useUpdatePodPricingConfig } from '@/hooks/usePodCatalog'
import { useAtelierSettings, useUpdateAtelierSettings } from '@/hooks/useAtelierSettings'
import { centsToEuros, eurosToCents } from '@/utils/atelierPricing'

import './PricingTab.css'

// ──────────────────────────────────────────────────────────────────────────────
// POD pricing — modèle tier-profit-absolu unique
// ──────────────────────────────────────────────────────────────────────────────

const profitTierSchema = z.object({
  max_cost_cents: z.number().int().min(0).nullable(),
  profit_min_cents: z.number().int().min(0, 'Le bénéfice ne peut pas être négatif'),
})

const podPricingSchema = z.object({
  etsy_fees_pct: z.number().min(0).max(99.9, 'Plafond < 100% (sinon division par zéro)'),
  gelato_plus_discount_pct: z.number().min(0).max(99.9, 'Plafond < 100% (sinon coût négatif)'),
  profit_tiers: z.array(profitTierSchema).min(1, 'Au moins une tranche est requise'),
})

type PodPricingFormValues = {
  etsy_fees_pct: number
  gelato_plus_discount_pct: number
  profit_tiers: Array<{
    max_cost_cents: number | null
    profit_min_cents: number
  }>
}

// Coûts Gelato typiques pour l'aperçu de calcul. Note : depuis la
// refonte tier-profit-absolu, la colonne "Net garanti" est l'input
// utilisateur direct → elle ne peut plus mentir, peu importe que les
// coûts simulés soient indicatifs.
const SIMULATION_COSTS_CENTS = [
  { label: 'A4 papier mat', cost_cents: 1000 },
  { label: 'A3 papier mat', cost_cents: 1500 },
  { label: 'A2 papier mat', cost_cents: 2500 },
  { label: '50×70 cadré noir', cost_cents: 6000 },
]

// Shipping main absorbé sur la zone gratuite — valeur indicative pour
// l'aperçu, ne reflète pas le shipping main réel (qui dépend des SKUs).
const SIMULATION_SHIPPING_MAIN_CENTS = 1000

function roundUpToEuroCents(cents: number): number {
  return Math.ceil(Math.ceil(cents) / 100) * 100
}

function interpolateProfitMin(
  baseCostCents: number,
  tiers: PodPricingFormValues['profit_tiers'],
): number {
  if (tiers.length === 0) return 0
  const finite = tiers.filter((t) => t.max_cost_cents !== null)
  const unbounded = tiers.find((t) => t.max_cost_cents === null)
  if (!unbounded) return finite[0]?.profit_min_cents ?? 0
  if (finite.length === 0 || baseCostCents > (finite[finite.length - 1]!.max_cost_cents ?? 0)) {
    return unbounded.profit_min_cents
  }
  if (baseCostCents <= (finite[0]!.max_cost_cents ?? 0)) {
    return finite[0]!.profit_min_cents
  }
  for (let i = 0; i < finite.length - 1; i++) {
    const capA = finite[i]!.max_cost_cents ?? 0
    const capB = finite[i + 1]!.max_cost_cents ?? 0
    if (capA < baseCostCents && baseCostCents <= capB) {
      const pA = finite[i]!.profit_min_cents
      const pB = finite[i + 1]!.profit_min_cents
      const ratio = (baseCostCents - capA) / (capB - capA)
      return Math.round(pA + ratio * (pB - pA))
    }
  }
  return unbounded.profit_min_cents
}

function previewPriceEtsy(values: PodPricingFormValues, baseCostCents: number): number {
  if (baseCostCents <= 0) return 0
  const feesFactor = 1 - values.etsy_fees_pct / 100
  if (feesFactor <= 0) return 0
  // Applique la remise Gelato+ côté client AVANT l'interpolation des tiers
  // (cohérent avec `pricing_service.compute_pod_retail_price_cents_from_config`).
  const discountFactor = Math.max(0, 1 - (values.gelato_plus_discount_pct || 0) / 100)
  const effectiveBase = Math.round(baseCostCents * discountFactor)
  const profitMin = interpolateProfitMin(effectiveBase, values.profit_tiers)
  const gross = effectiveBase + SIMULATION_SHIPPING_MAIN_CENTS + profitMin
  return roundUpToEuroCents(gross / feesFactor)
}

function PodPricingSection() {
  const { data: config, isLoading } = usePodPricingConfig()
  const { mutate: save, isPending } = useUpdatePodPricingConfig()

  const methods = useForm<PodPricingFormValues>({
    resolver: zodResolver(podPricingSchema),
    values: config
      ? {
          etsy_fees_pct: config.etsy_fees_pct,
          gelato_plus_discount_pct: config.gelato_plus_discount_pct ?? 0,
          profit_tiers: config.profit_tiers.map((t) => ({
            max_cost_cents: t.max_cost_cents ?? null,
            profit_min_cents: t.profit_min_cents,
          })),
        }
      : {
          etsy_fees_pct: 12,
          gelato_plus_discount_pct: 0,
          profit_tiers: [
            { max_cost_cents: 1500, profit_min_cents: 800 },
            { max_cost_cents: 3000, profit_min_cents: 1200 },
            { max_cost_cents: 6000, profit_min_cents: 1800 },
            { max_cost_cents: null, profit_min_cents: 2500 },
          ],
        },
  })

  const watched = methods.watch()
  const tiersFieldArray = useFieldArray({
    control: methods.control,
    name: 'profit_tiers',
  })

  const onSubmit = methods.handleSubmit((values) => {
    save(values, {
      onSuccess: (resp) => {
        const enq = resp.refresh_summary?.enqueued ?? 0
        const skipped = resp.refresh_summary?.skipped_conflict ?? 0
        if (enq > 0) {
          toast.success(
            `Tarifs mis à jour. ${enq} listing${enq > 1 ? 's' : ''} Etsy en cours de republish` +
              (skipped > 0
                ? ` (${skipped} reporté${skipped > 1 ? 's' : ''} — job en file déjà actif)`
                : ''),
          )
        } else {
          toast.success('Tarifs mis à jour — aucun listing actif à refresh')
        }
      },
      onError: () => {
        toast.error('Erreur lors de la sauvegarde')
      },
    })
  })

  if (isLoading) {
    return <p className="ui-text-muted">Chargement de la configuration…</p>
  }

  const migrationReviewPending = config?.migration_review_pending === true

  return (
    <section className="pricing-tab-section">
      <header className="settings-section-header">
        <div>
          <h2 className="ui-h4 settings-section-title">Marge atelier &amp; frais Etsy</h2>
          <p className="ui-body ui-text-muted settings-section-subtitle">
            Pour chaque tranche de coût Gelato (impression seule), indique le bénéfice net minimum
            que tu veux toucher après absorption du shipping zone gratuite et des frais Etsy. Le
            système calcule automatiquement le prix Etsy nécessaire pour garantir ce net.
          </p>
        </div>
      </header>

      {migrationReviewPending && (
        <div className="ui-alert ui-alert--warning" role="alert">
          <strong>Tarification migrée vers un nouveau modèle.</strong> Au lieu d&apos;entrer un
          pourcentage de marge, tu entres maintenant le bénéfice net minimum que tu veux toucher par
          tranche de coût Gelato. Vérifie les seuils ci-dessous et sauvegarde une fois pour
          confirmer (la bannière disparaîtra).
        </div>
      )}

      <FormProvider {...methods}>
        <form onSubmit={onSubmit} className="pricing-tab-form">
          {/* ── Frais Etsy (compensation universelle, affecte tous medium_kind) ── */}
          <Field
            label="Frais Etsy estimés (%)"
            helper="Frais Etsy retenus sur le prix de vente (transaction + processing). Affecte TOUTES les variantes (POD, digital, original, limited) via la compensation universelle."
            error={methods.formState.errors.etsy_fees_pct?.message}
          >
            {(fieldProps) => (
              <Input
                type="number"
                step="0.1"
                min="0"
                max="99.9"
                {...methods.register('etsy_fees_pct', { valueAsNumber: true })}
                {...fieldProps}
              />
            )}
          </Field>

          {/* ── Remise Gelato+ (appliquée côté client, l'API ne la reflète pas) ── */}
          <Field
            label="Remise Gelato+ (%)"
            helper="Pourcentage de remise membre Gelato+ appliqué sur le coût Gelato. L'API Gelato /v4/orders:quote retourne le prix régulier sans la remise — on l'applique côté portail. Mettre à 0 si tu n'es pas abonnée Gelato+."
            error={methods.formState.errors.gelato_plus_discount_pct?.message}
          >
            {(fieldProps) => (
              <Input
                type="number"
                step="0.1"
                min="0"
                max="99.9"
                {...methods.register('gelato_plus_discount_pct', { valueAsNumber: true })}
                {...fieldProps}
              />
            )}
          </Field>

          {/* ── Tranches profit minimum ───────────────────────────────────── */}
          <div className="pricing-tab-tiers">
            <label className="ui-body pricing-tab-tiers-label">
              Bénéfice atelier net minimum par tranche
            </label>
            <p className="ui-body-sm ui-text-muted pricing-tab-tiers-helper">
              Pour chaque tranche, indique le coût Gelato <em>maximum</em> (en €) et le bénéfice net
              minimum (en €) que tu veux toucher. La dernière tranche doit avoir le coût max vide (=
              ∞, capte tout le reste). Tranches ordonnées par coût croissant.
            </p>
            <div className="pricing-tab-tier-row pricing-tab-tier-row--head">
              <span className="pricing-tab-tier-header">Coût Gelato max (€)</span>
              <span aria-hidden="true" />
              <span className="pricing-tab-tier-header">Bénéfice net min (€)</span>
              <span aria-hidden="true" />
              <span aria-hidden="true" />
            </div>
            {tiersFieldArray.fields.map((field, idx) => {
              const isLast = idx === tiersFieldArray.fields.length - 1
              return (
                <div key={field.id} className="pricing-tab-tier-row">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder={isLast ? '∞ (tout le reste)' : 'Coût max € (ex 15)'}
                    disabled={isLast}
                    value={(() => {
                      const v = watched.profit_tiers[idx]?.max_cost_cents
                      return v === null || v === undefined ? '' : (v / 100).toString()
                    })()}
                    onChange={(e) => {
                      const raw = e.target.value
                      const cents = raw === '' ? null : Math.round(parseFloat(raw) * 100)
                      methods.setValue(
                        `profit_tiers.${idx}.max_cost_cents`,
                        Number.isNaN(cents) ? null : cents,
                        { shouldDirty: true },
                      )
                    }}
                  />
                  <span aria-hidden="true">→</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Bénéfice net € (ex 8)"
                    value={(() => {
                      const v = watched.profit_tiers[idx]?.profit_min_cents
                      return v === undefined ? '' : (v / 100).toString()
                    })()}
                    onChange={(e) => {
                      const raw = e.target.value
                      const cents = raw === '' ? 0 : Math.round(parseFloat(raw) * 100)
                      methods.setValue(
                        `profit_tiers.${idx}.profit_min_cents`,
                        Number.isNaN(cents) ? 0 : cents,
                        { shouldDirty: true },
                      )
                    }}
                  />
                  <span aria-hidden="true">€</span>
                  {tiersFieldArray.fields.length > 1 && !isLast && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => tiersFieldArray.remove(idx)}
                    >
                      Retirer
                    </Button>
                  )}
                </div>
              )
            })}
            <Button
              type="button"
              variant="quiet"
              size="sm"
              onClick={() => {
                // Insère avant la dernière tranche (qui doit rester max=null).
                const lastIdx = tiersFieldArray.fields.length - 1
                tiersFieldArray.insert(lastIdx, {
                  max_cost_cents: 4500,
                  profit_min_cents: 1500,
                })
              }}
            >
              + Ajouter une tranche
            </Button>
          </div>

          {/* ── Table de simulation — voir le résultat avant save ─────────── */}
          <div className="pricing-tab-simulation">
            <h3 className="ui-body pricing-tab-simulation-title">
              Aperçu sur 4 formats typiques (coûts Gelato approximatifs, shipping main simulé à{' '}
              {(SIMULATION_SHIPPING_MAIN_CENTS / 100).toFixed(2)} €)
            </h3>
            <table className="pricing-tab-simulation-table">
              <thead>
                <tr>
                  <th>Format</th>
                  <th>Coût Gelato</th>
                  <th>Shipping absorbé</th>
                  <th>Prix Etsy</th>
                  <th>Net garanti</th>
                </tr>
              </thead>
              <tbody>
                {SIMULATION_COSTS_CENTS.map((s) => {
                  const etsyCents = previewPriceEtsy(watched, s.cost_cents)
                  const profitMinCents = interpolateProfitMin(s.cost_cents, watched.profit_tiers)
                  return (
                    <tr key={s.label}>
                      <td>{s.label}</td>
                      <td>{(s.cost_cents / 100).toFixed(2)} €</td>
                      <td>{(SIMULATION_SHIPPING_MAIN_CENTS / 100).toFixed(2)} €</td>
                      <td>
                        <strong>{(etsyCents / 100).toFixed(2)} €</strong>
                      </td>
                      <td>{(profitMinCents / 100).toFixed(2)} €</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            <p className="ui-body-sm ui-text-muted">
              Aperçu indicatif sur coûts fictifs — le shipping main réel est calculé par SKU au
              moment du publish (max des frais Gelato sur la zone gratuite configurée).
            </p>
          </div>

          <Button type="submit" variant="primary" disabled={isPending}>
            {isPending ? 'Sauvegarde + refresh massif…' : 'Enregistrer'}
          </Button>
        </form>
      </FormProvider>
    </section>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// Digital default — prix par défaut variante digitale unitaire + rescale
// ──────────────────────────────────────────────────────────────────────────────

const digitalDefaultSchema = z.object({
  digital_default: z.union([z.literal(''), z.number().min(0)]),
})

type DigitalDefaultFormValues = z.infer<typeof digitalDefaultSchema>

function DigitalDefaultSection() {
  const settingsQuery = useAtelierSettings()
  const updateMutation = useUpdateAtelierSettings()

  const settings = settingsQuery.data

  const methods = useForm<DigitalDefaultFormValues>({
    resolver: zodResolver(digitalDefaultSchema),
    values: {
      digital_default: settings?.config.digital_default_price_cents
        ? centsToEuros(settings.config.digital_default_price_cents)
        : '',
    },
  })

  const onSubmit = methods.handleSubmit((values) => {
    const cents = eurosToCents(values.digital_default) ?? 0
    updateMutation.mutate(
      { digital_default_price_cents: cents },
      {
        onSuccess: (resp) => {
          const rescaled = resp.rescale_summary?.rescaled ?? 0
          const enq = resp.rescale_summary?.refresh_enqueued ?? 0
          if (rescaled > 0) {
            toast.success(
              `Prix défaut digital enregistré. ${rescaled} variante${rescaled > 1 ? 's' : ''} digitale${rescaled > 1 ? 's' : ''} rescalée${rescaled > 1 ? 's' : ''} proportionnellement` +
                (enq > 0
                  ? `. ${enq} listing${enq > 1 ? 's' : ''} Etsy en cours de republish.`
                  : '.'),
            )
          } else {
            toast.success('Prix défaut digital enregistré')
          }
        },
        onError: (err) => {
          toast.error(err.detail ?? 'Mise à jour impossible.')
        },
      },
    )
  })

  if (settingsQuery.isLoading) {
    return <p className="ui-text-muted">Chargement…</p>
  }

  return (
    <section className="pricing-tab-section">
      <header className="settings-section-header">
        <div>
          <h2 className="ui-h4 settings-section-title">Prix défaut digital</h2>
          <p className="ui-body ui-text-muted settings-section-subtitle">
            Sert de tarif de référence atelier pour les variantes digitales unitaires.
            <strong> Important</strong> : changer cette valeur applique une homothétie
            proportionnelle sur <em>toutes</em> les variantes digitales existantes. Ex : tu passes
            de 8,30 € à 12,30 € (+48 %), une variante actuellement à 18,30 € devient 27,12 €
            automatiquement.
          </p>
          <p className="ui-body ui-text-muted settings-section-subtitle">
            <em>Note bundles</em> : ce défaut concerne les variantes digitales unitaires (vente
            d&apos;un fichier seul). Quand la feature « bundles digitaux » arrivera, le prix bundle
            vivra à part — pas d&apos;écrasement croisé.
          </p>
        </div>
      </header>

      <FormProvider {...methods}>
        <form onSubmit={onSubmit} className="pricing-tab-form">
          <Field
            label="Prix défaut digital (€ net atelier)"
            helper="Vide ou 0 = pas de défaut. Le prix affiché Etsy sera ajusté pour compenser les frais."
          >
            {(fieldProps) => (
              <Input
                type="number"
                min={0}
                step={0.01}
                placeholder="—"
                {...methods.register('digital_default', {
                  setValueAs: (v) => (v === '' || v === null || v === undefined ? '' : Number(v)),
                })}
                {...fieldProps}
              />
            )}
          </Field>

          <Button
            type="submit"
            variant="primary"
            disabled={updateMutation.isPending || !methods.formState.isDirty}
          >
            {updateMutation.isPending ? 'Enregistrement + rescale…' : 'Enregistrer'}
          </Button>
        </form>
      </FormProvider>
    </section>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// Composant racine
// ──────────────────────────────────────────────────────────────────────────────

export default function PricingTab() {
  return (
    <div className="pricing-tab">
      <PodPricingSection />
      <DigitalDefaultSection />
    </div>
  )
}
